import type { Car } from "@/types/car";

const CURRENCY_SYMBOLS: Record<string, string> = {
  CNY: "¥",
  KRW: "₩",
  JPY: "¥",
};

export function formatPrice(price: number, currency: string): string {
  const symbol = CURRENCY_SYMBOLS[currency] || currency;
  const formatted = price.toLocaleString("ru-RU");
  return `${symbol} ${formatted}`;
}

const COUNTRY_LABELS: Record<string, string> = {
  china: "Китай",
  korea: "Южная Корея",
  japan: "Япония",
};

export function getCountryLabel(country: string): string {
  return COUNTRY_LABELS[country] || country;
}

const COUNTRY_FLAGS: Record<string, string> = {
  china: "🇨🇳",
  korea: "🇰🇷",
  japan: "🇯🇵",
};

export function getCountryFlag(country: string): string {
  return COUNTRY_FLAGS[country] || "";
}

export function generateSlug(folderName: string): string {
  return folderName
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "");
}

const TRANSMISSION_LABELS: Record<string, string> = {
  AT: "Автомат",
  MT: "Механика",
  CVT: "Вариатор",
};

// @rule INV-10 (catalog-specs-normalization): transmission is shown ONLY when it
// is a real gearbox type (AT/MT/CVT). Any other stored value (e.g. a mis-captured
// "5-Местный" seat count, or a raw selector letter) returns "" so the caller HIDES
// the field — never echo a raw value, never guess a type. The backfill rewrites
// such values to "" as well; this is the display-side guard.
export function getTransmissionLabel(t: string): string {
  return TRANSMISSION_LABELS[t] ?? "";
}

// RULE: internal primitive (strip "Used " + trim) — it does NOT fold case. Anything a
// buyer READS goes through canonicalBrand instead (TAXONOMY-3, step 06): display callers
// were migrated 2026-07-28, leaving only canonicalBrand, buildCarSlug and getRelatedCars here.
export function cleanBrand(brand: string): string {
  return brand.replace(/^Used\s+/i, "").trim();
}

/**
 * Known brand spelling/case variants → the ONE canonical display form. Keys are
 * pre-normalised (cleaned + lower-cased). Seeded with the only two collisions the
 * live catalog actually carries: "AUDI" next to "Audi", "CHANGAN" next to "Changan".
 * @rule This map holds ONLY collisions — it is not a whitelist of known brands. An
 *       absent brand is a real brand and passes through unchanged (TAXONOMY-3), so a
 *       new marque (Xiaomi, Zeekr) needs no code change. A new case-collision is
 *       fixed by adding ONE line here, never by a parallel map elsewhere.
 */
const BRAND_ALIASES: Record<string, string> = {
  "audi": "Audi",
  "changan": "Changan",
};

/**
 * Collapse a raw brand string to its canonical display spelling.
 * @section brand
 * @input  brand — raw Car.brand, possibly "Used "-prefixed and in a variant case.
 * @output The canonical brand for a known collision, otherwise the cleaned brand
 *         unchanged. Never blank for a non-empty brand.
 * @important Unlike the body-type / fuel normalisers there is NO "unfixable" here:
 *            brands are open-ended, so the default is pass-through, not drop — an
 *            unrecognised brand still reaches the filter and the card (TAXONOMY-3).
 *            Built ON TOP of cleanBrand (the "Used " strip + trim stays in one place);
 *            cleanBrand itself is unchanged, so its existing callers are unaffected.
 */
export function canonicalBrand(brand: string): string {
  const cleaned = cleanBrand(brand);
  const key = cleaned.toLowerCase();
  return BRAND_ALIASES[key] ?? cleaned;
}

/**
 * The ONE display name of a car — brand + model + year, without the brand stutter.
 * @section brand
 * @universal knowledge/universals/tools.md#cardisplayname
 * @input  car — anything carrying brand / model / year (a Car, or a plain object);
 *         options.year — pass `false` to omit the trailing year (default: include it).
 * @output The title a buyer reads: "Tank 300 2024", "Land Rover Range Rover Sport 2025",
 *         "Mercedes-Benz C-Class 2026". Empty model → brand alone; empty brand → model
 *         alone; nothing to show → "".
 * @important DISPLAY ONLY. Car.brand, Car.model, folderName, slugs and URLs are never
 *            rewritten by this — the stored data and every existing address stay exactly
 *            as they are. This function is where the two fields MEET, and that is the only
 *            place the duplication can be fixed once for every brand.
 * @important The prefix rule is WHOLE-WORD: the brand is dropped only when the model IS the
 *            brand or starts with the brand followed by a space ("Tank" + "Tank 300" →
 *            "Tank 300"), never on a partial word ("Tank" + "Tanker X" stays "Tank Tanker X").
 * @important "Land Rover" + "Range Rover Sport" renders FULL — a partial word overlap is not
 *            a duplicate, "Land Rover Range Rover Sport" is the official name (owner decision
 *            2026-08-15, same as Авто.ру / Дром). Do not "improve" this into a fuzzy match.
 * @important Brand handling goes through canonicalBrand and nothing else (TAXONOMY-3: an
 *            unknown brand passes through, there is no whitelist here). The model gets the
 *            same "Used " strip cleanBrand applies to brands, inline — sources put that
 *            prefix on both fields.
 */
export function carDisplayName(
  car: Pick<Car, "brand" | "model" | "year">,
  options?: { year?: boolean },
): string {
  const brand = canonicalBrand(car.brand ?? "");
  const model = (car.model ?? "").replace(/^Used\s+/i, "").trim();

  let name: string;
  if (!brand) {
    name = model;
  } else if (!model) {
    name = brand;
  } else {
    const brandKey = brand.toLowerCase();
    const modelKey = model.toLowerCase();
    const modelCarriesBrand = modelKey === brandKey || modelKey.startsWith(`${brandKey} `);
    name = modelCarriesBrand ? model : `${brand} ${model}`;
  }

  if (!name) return "";
  return options?.year !== false && car.year > 0 ? `${name} ${car.year}` : name;
}

const COUNTRY_GENITIVE: Record<string, string> = {
  china: "Китая",
  korea: "Кореи",
  japan: "Японии",
};

export function getCountryGenitive(country: string): string {
  return COUNTRY_GENITIVE[country] || country;
}

/**
 * Returns true when the car was added to the catalog within the
 * last `days` days. Used to decide the "Новинка" badge.
 * Reads car.createdAt (ISO 8601 string). If createdAt is missing,
 * malformed, or in the future, returns false (no badge).
 */
export function isRecentCar(car: Car, days: number = 7): boolean {
  try {
    if (!car.createdAt) return false;
    const created = new Date(car.createdAt).getTime();
    if (Number.isNaN(created)) return false;
    const diff = Date.now() - created;
    if (diff < 0) return false;
    return diff < days * 86_400_000;
  } catch {
    return false;
  }
}

/**
 * Returns cars related to `currentCar` filtered by (1) same country,
 * (2) optional partial brand match (case-insensitive substring),
 * (3) excluding currentCar by id. Result is sliced by offset and limit.
 * Returns an array — empty if no matches.
 *
 * Used by:
 * - CarSidebarRelated component (limit=3, offset=0)
 * - TailContentPreview kind="cars" filter (limit=3, offset can be 0 or 3
 *   to avoid duplicates with the sidebar)
 */
export function getRelatedCars(
  allCars: Car[],
  currentCar: Car,
  options: { limit: number; offset?: number; matchBrand?: boolean } = { limit: 3 },
): Car[] {
  const { limit, offset = 0, matchBrand = true } = options;
  let filtered = allCars.filter((c) => c.country === currentCar.country);
  if (matchBrand && currentCar.brand) {
    const needle = cleanBrand(currentCar.brand).toLowerCase();
    filtered = filtered.filter((c) => cleanBrand(c.brand).toLowerCase().includes(needle));
  }
  filtered = filtered.filter((c) => c.id !== currentCar.id);
  return filtered.slice(offset, offset + limit);
}
